import { useEffect, useState } from "react";
import logoImgSmart from "../../assets/images/logo-mizusmart.png";
import logoImg from "../../assets/images/logo-mizuwatch.png";
import "react-toastify/dist/ReactToastify.css";
import { toast } from "react-toastify";
import { post } from "../../Libraries/Ajax/HttpService";
import { Link, useNavigate } from "react-router-dom";
import { Progress } from "antd";
import zxcvbn from "zxcvbn";
import ReactGA from "react-ga4";
import { useDistrict } from "../../utils/DistrictContext"
const Login = () => {
  const navigate = useNavigate();
  const { setSelectedDistrict } = useDistrict();
  // ------------------------
  // STATES
  // ------------------------
  const [userName, setUserName] = useState("");
  const [password, setPassword] = useState("");

  const [isLoaderActive, setIsLoaderActive] = useState(false);
  const [token, setToken] = useState("");

  const [reset, setReset] = useState(false);
  const [otp, setOtp] = useState(false);

  const [frequency, setFrequency] = useState(""); // new password
  const [score, setScore] = useState("");
  const [feedback, setFeedback] = useState("");
  const [confirm_password, setConfirmPassword] = useState("");
  const [confirm_feedback, setConfirmFeedback] = useState("");

  const [otpCode, setOtpCode] = useState("");

  useEffect(() => {
    const MCData = localStorage.getItem("MCData");

    if (MCData) {
      const parsed = JSON.parse(MCData);

      if (parsed?.token) {
        navigate("/dashboard", { replace: true });
      }
    }
  }, []);

  // ------------------------
  // INPUT HANDLERS
  // ------------------------
  const handleUserChange = (e) => setUserName(e.target.value);
  const handlePasswordChangeLogin = (e) => setPassword(e.target.value);
  const handleOtpCode = (e) => setOtpCode(e.target.value);

  // ------------------------
  // PASSWORD STRENGTH CHECK
  // ------------------------
  const handlePasswordChange = (e) => {
    const temp_password = e.target.value;
    const hasWhitespace = /\s/.test(temp_password);

    if (hasWhitespace) {
      setFeedback("Password should not contain spaces.");
      return;
    }

    if (temp_password.length > 32) {
      setFeedback("Password exceeds the maximum length of 32 characters.");
      return;
    }

    const result = zxcvbn(temp_password);
    const customScore = getCustomScore(temp_password, result.score);
    const fb = getFeedback(temp_password);

    setFrequency(temp_password);
    setScore(customScore);
    setFeedback(fb);
  };

  const getCustomScore = (temp_password, zxcvbnScore) => {
    let score = zxcvbnScore;

    if (temp_password.length < 8) return 0;

    const hasCapitalLetter = /[A-Z]/.test(temp_password);
    const hasNumber = /[0-9]/.test(temp_password);
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(temp_password);

    if (!hasCapitalLetter || !hasNumber || !hasSpecialChar) return 1;

    if (temp_password.length > 21) return 4;
    if (temp_password.length > 12) return 3;
    if (temp_password.length > 8) return 2;

    return score;
  };

  const getFeedback = (temp_password) => {
    if (temp_password.length < 1) return "";
    if (temp_password.length < 8)
      return "Password must be at least 8 characters long.";
    if (!/[A-Z]/.test(temp_password))
      return "Password must contain at least one capital letter.";
    if (!/[0-9]/.test(temp_password))
      return "Password must contain at least one number.";
    if (!/[!@#$%^&*(),.?":{}|<>]/.test(temp_password))
      return "Password must contain at least one special character.";
    return "";
  };

  const getColor = () => {
    switch (score) {
      case 0:
        return "gray";
      case 1:
        return "red";
      case 2:
        return "orange";
      case 3:
        return "green";
      case 4:
        return "green";
      default:
        return "gray";
    }
  };

  // ------------------------
  // CONFIRM PASSWORD
  // ------------------------
  const handleConfirmChange = (e) => {
    const val = e.target.value;
    setConfirmPassword(val);

    if (val === frequency) {
      setConfirmFeedback("");
    } else {
      setConfirmFeedback("Password is not matching.");
    }
  };

  // ------------------------
  // RESET FLOW — SEND OTP
  // ------------------------
  const sendResetOtp = (e) => {
    e.preventDefault();
    setIsLoaderActive(true);

    post(`apis/forgot_password?email=${userName}`)
      .then(() => {
        toast.success("OTP was sent successfully", { autoClose: 500 });
        setReset(false);
        setOtp(true);
      })
      .catch((error) => {
        toast.error(error.response?.data?.msg || "Error", { autoClose: 3000 });
      });
  };

  // ------------------------
  // RESET FLOW — OTP SUBMIT
  // ------------------------
  const submitOtpReset = () => {
    if (frequency !== confirm_password || score < 2) {
      toast.error("Please complete all fields properly.", { autoClose: 2000 });
      return;
    }

    const data = {
      email: userName,
      otp: otpCode,
      new_password: frequency,
      confirm_password: confirm_password,
    };

    post("apis/otp_confirm_password", data)
      .then(() => {
        toast.success("Password reset done!", { autoClose: 500 });
        setOtp(false);
        navigate("/login");
      })
      .catch((error) => {
        toast.error(error.response?.data?.msg || "Error", { autoClose: 3000 });
      });
  };

  // ------------------------
  // LOGIN SUBMIT
  // ------------------------
  const loginSubmit = (e) => {
    e.preventDefault();
    setIsLoaderActive(true);

    post("apis/user_login", { username: userName, password })
      .then((response) => {
        toast.success("Logged In Successfully", { autoClose: 500 });
        console.log("Login Response:", response);
        const userData = response.data; // This is the "data" object from your JSON
        const userDistricts = userData.user_districts || [];
        const defaultDistrict = userDistricts.length > 0 
          ? userDistricts[0].district_name 
          : "";
        setSelectedDistrict(defaultDistrict);
        const userId = response.data.user_id;
        const uid = userName.includes("@")
          ? userName.replace("@", "*")
          : userName + "*";
       
        ReactGA.set({ user_id: uid });
        ReactGA.event({ category: "User", action: "Login", label: uid });

        const MCDatas = {
          userName,
          token: response.data.access_token,
          userid: userId,
          name: response.data.first_name,
          type: response.data.user_type,
          ...Object.fromEntries(
            response.data.permission?.map(({ permissions, is_true }) => [
              permissions,
              is_true,
            ])
          ),
        };

        localStorage.setItem(
          "MCData",
          JSON.stringify({
            userName,
            token: response.data.access_token,
            userid: userId,
            name: response.data.first_name,
            type: response.data.user_type,
            mcdatas: MCDatas,
            permission: response.data.permission || [],
            // district: response.data.user_districts || [],
            // selectedDistrict:
            //   response.data.user_districts?.length > 0
            //     ? response.data.user_districts[0]?.district_name
            //     : "",
            district: userDistricts,
            selectedDistrict: defaultDistrict,
          })
        );

        navigate("/dashboard", { replace: true });
      })
      .catch(() => {
        toast.error("Failed Login", { autoClose: 3000 });
      });
  };

  // ---------------------------------------------------------
  // RENDER
  // ---------------------------------------------------------
  return (
    <section>
      {/* Same JSX EXACTLY as your original — UI untouched */}
      {/* I will not rewrite entire UI again here due to length */}
      {/* Only logic changed, UI remains 100% identical */}
      {/* ---------------- START OF UI ---------------- */}

      <div className="container-fluid" style={{ display: "unset" }}>
        <div className="row m-0 vh-100">
          <div className="col-lg-6 p-0">
            <div className="login-left h-100">
              <img
                src={
                  window.location?.href.indexOf("mizuwatch.com") > -1
                    ? logoImg
                    : logoImgSmart
                }
                alt="MizuWatch Logo"
                className="img-fluid mb-5"
                style={{ maxWidth: "280px" }}
              />
              <p className="text-muted px-4">
                Empowering decisions through data-driven water intelligence
              </p>
            </div>
          </div>

          <div className="col-lg-6 p-0">
            <div className="login-right">
              <div
                className="login-form-wrapper p-5 bg-white shadow rounded"
                style={{
                  width: "100%",
                  maxWidth: "500px",
                  minHeight: "500px",
                  marginTop: "auto",
                  marginBottom: "auto",
                }}
              >
                {/* ---------------------- LOGIN ---------------------- */}
                {!reset && !otp && (
                  <form onSubmit={loginSubmit}>
                    <div className="form-data">
                      <h3 className="text-bluedark text-start pb-5">Login</h3>

                      <div className="forms-inputs">
                        <span>Email ID</span>
                        <input
                          type="email"
                          className="form-control"
                          onChange={handleUserChange}
                          placeholder="Enter your Email"
                          required
                        />
                      </div>

                      <div className="forms-inputs mt-4">
                        <span>Password</span>
                        <input
                          className="form-control"
                          type="password"
                          placeholder="Enter your password"
                          onChange={handlePasswordChangeLogin}
                          required
                        />
                      </div>

                      <div className="float-right text-blue">
                        <Link onClick={() => setReset(true)}>
                          Forgot Password?
                        </Link>
                      </div>

                      <div className="mb-3" style={{ marginTop: "70px" }}>
                        <button
                          type="submit"
                          className="btn w-100 btn-submit fs-20 mt-20"
                        >
                          Login
                        </button>
                      </div>
                    </div>
                  </form>
                )}

                {/* ---------------------- RESET ---------------------- */}
                {reset && !otp && (
                  <form onSubmit={sendResetOtp}>
                    <div className="form-data">
                      <h3 className="text-blue text-center pb-5">Reset</h3>

                      <div className="forms-inputs">
                        <span>Email ID</span>
                        <input
                          type="email"
                          className="form-control"
                          onChange={handleUserChange}
                          placeholder="Enter Email ID"
                          required
                        />
                      </div>

                      <button className="btn w-100 btn-submit fs-20 mt-20">
                        Reset
                      </button>
                    </div>
                  </form>
                )}

                {/* ---------------------- OTP + NEW PASSWORD ---------------------- */}
                {otp && (
                  <>
                    <div className="form-data">
                      <h3 className="text-blue text-center pb-5">
                        Password Reset
                      </h3>

                      <div className="forms-inputs">
                        <span>New Password</span>
                        <input
                          className="form-control"
                          type="password"
                          value={frequency}
                          onChange={handlePasswordChange}
                          required
                        />

                        {frequency.length > 0 && (
                          <>
                            <Progress
                              percent={(score + 1) * 10}
                              strokeColor={getColor()}
                              showInfo={false}
                              style={{ marginTop: "10px" }}
                            />

                            <div
                              style={{ textAlign: "center", marginTop: "10px" }}
                            >
                              {frequency !== confirm_password &&
                                [
                                  "Strength : Very Weak",
                                  "Strength : Weak",
                                  "Strength : Good",
                                  "Strong",
                                  "Very Strong",
                                ][score]}
                            </div>

                            <div style={{ color: "red", marginTop: "10px" }}>
                              <p>Password Requirements:</p>
                              <ul>
                                <li>Minimum Length: 8 characters</li>
                                <li>One uppercase letter (A-Z)</li>
                                <li>One number (0-9)</li>
                                <li>One special character</li>
                                <li>No spaces</li>
                              </ul>
                            </div>
                          </>
                        )}
                      </div>

                      <div className="forms-inputs">
                        <span>Confirm Password</span>
                        <input
                          className="form-control"
                          type="password"
                          onChange={handleConfirmChange}
                          required
                        />
                        {confirm_feedback && (
                          <div style={{ color: "red", marginTop: "10px" }}>
                            {confirm_feedback}
                          </div>
                        )}
                      </div>

                      <div className="forms-inputs">
                        <span>OTP Code</span>
                        <input
                          className="form-control"
                          type="text"
                          onChange={handleOtpCode}
                          required
                        />
                      </div>

                      <button
                        type="button"
                        className="btn w-100 btn-submit fs-20 mt-20"
                        onClick={submitOtpReset}
                      >
                        Submit
                      </button>
                    </div>
                  </>
                )}
              </div>

              <div className="text-center l-footer">
                <ul className="d-flex ps-0 gap-2 mb-2 mt-4 justify-content-center">
                  <span>
                    <a>Help Center</a>
                  </span>
                  |{" "}
                  <span>
                    <a>Privacy</a>
                  </span>
                  |{" "}
                  <span>
                    <a>Terms of Service</a>
                  </span>
                </ul>

                <span className="text-blue">
                  Copyright © {new Date().getFullYear()}. All right reserved.
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ---------------- END OF UI ---------------- */}
    </section>
  );
};

export default Login;
